import React, { useCallback } from 'react';
import { motion } from 'motion/react';
import { cn } from '../../utils/cn';

/**
 * Toggle Component Props
 */
export interface ToggleProps {
  /** Whether the toggle is checked/on */
  checked: boolean;
  /** Callback when toggle state changes */
  onChange: (checked: boolean) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Label for the toggle */
  label?: string;
  /** Description text below the label */
  description?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional class name for the container */
  className?: string;
}

// Size configurations
const sizeConfig = {
  sm: {
    track: 'w-9 h-5',
    circle: 'w-4 h-4',
    translate: 'translateX(16px)',
  },
  md: {
    track: 'w-11 h-6',
    circle: 'w-5 h-5',
    translate: 'translateX(20px)',
  },
} as const;

/**
 * Toggle - An accessible toggle switch component
 *
 * Features:
 * - Pill-shaped track with sliding circle
 * - Smooth animation transitions
 * - StellarTrail primary color when on
 * - Keyboard accessible (Space/Enter to toggle)
 * - ARIA switch role for screen readers
 *
 * @example
 * ```tsx
 * <Toggle
 *   checked={isEnabled}
 *   onChange={setIsEnabled}
 *   label="Enable notifications"
 *   description="Receive email notifications for important updates"
 * />
 * ```
 */
export const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  label,
  description,
  size = 'md',
  className,
}) => {
  const config = sizeConfig[size];

  const handleClick = useCallback(() => {
    if (!disabled) {
      onChange(!checked);
    }
  }, [checked, disabled, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChange(!checked);
      }
    },
    [checked, disabled, onChange]
  );

  const toggleButton = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={label}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={cn(
        // Base track styles
        'relative inline-flex flex-shrink-0 rounded-full',
        'transition-colors duration-200 ease-in-out',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-st-primary',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-st-dark',
        config.track,
        // State colors
        checked ? 'bg-st-primary' : 'bg-slate-600',
        // Disabled state
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer'
      )}
    >
      {/* Sliding circle */}
      <motion.span
        className={cn(
          'pointer-events-none inline-block rounded-full shadow-sm',
          config.circle,
          checked ? 'bg-white' : 'bg-slate-400'
        )}
        initial={false}
        animate={{
          x: checked ? (size === 'sm' ? 16 : 20) : 2,
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30,
        }}
        style={{ marginTop: size === 'sm' ? 2 : 2 }}
      />
    </button>
  );

  // If no label, return just the toggle button
  if (!label) {
    return (
      <div className={className}>
        {toggleButton}
      </div>
    );
  }

  // Return toggle with label and optional description
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4',
        disabled && 'opacity-50',
        className
      )}
    >
      <div className="flex-1 min-w-0">
        <label
          className={cn(
            'text-sm font-medium',
            disabled ? 'text-slate-400' : 'text-white',
            !disabled && 'cursor-pointer'
          )}
          onClick={handleClick}
        >
          {label}
        </label>
        {description && (
          <p className="text-xs text-slate-400 mt-0.5">{description}</p>
        )}
      </div>
      {toggleButton}
    </div>
  );
};

export default Toggle;
